import React, { useState, useEffect, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Page, Card, IndexTable, Link, Badge, Layout, Text, FormLayout, TextField, Select, Stack, Banner } from "@shopify/polaris";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../components/global/appContext";
import { TitleBar } from "@shopify/app-bridge-react";
import useLanguageSelector from "../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../components/components/LanguageSelector";

import PlaceTable from "./PlaceTable";
import AreaFormSkeleton from "./AreaFormSkeleton";
import SEOEditor from "./SeoEditor";
import lmsAPI from "../../../services/lmsAPI";
import useCompare from "../../../hooks/useCompare";
import { useGetLanguageSettings } from "../../../hooks/queries/languageSettings";

export default function AreaForm(props) {
  const { t } = useTranslation();
  const { languageTranslateIconUrl } = useContext(AppContext);
  const appContext = useContext(AppContext);
  const queryClient = useQueryClient();
  let { id } = useParams();
  const queryKey = [props.resourceKey, id];
  const { data: languageSettings } = useGetLanguageSettings();
  
  const [selectedNameLocale, setSelectedNameLocale] = useState("en");

  async function areaRequest() {
    const res = await lmsAPI.get(props.resourceUrl);
    return res.data;
  }

  const { data, isLoading, isError } = useQuery(queryKey, areaRequest);
  const [area, setArea] = useState(data);
  const [originalData, setOriginalData] = useState(null);
  
  useEffect(() => {
    if (data) {
      setArea(data);
      // Create a deep copy of the original data to prevent mutation
      setOriginalData(JSON.parse(JSON.stringify(data)));
    }
  }, [data]);

  // Use the proper language selector hook
  const {
    currentLocale,
    isLocalizationEnabled,
    localeOptions,
    getLocaleDisplayName
  } = useLanguageSelector(area);

  // Initialize selected locale when data loads
  useEffect(() => {
    if (currentLocale) {
      setSelectedNameLocale(currentLocale);
    }
  }, [currentLocale]);

  async function saveAreaRequest() {
    const res = await lmsAPI.put(props.resourceUrl, {
      area: area,
    });
    return res.data;
  }
  const saveArea = useMutation(saveAreaRequest, {
    onSuccess: (data) => {
      setArea(data);
      setOriginalData(JSON.parse(JSON.stringify(data))); // Update baseline after successful save
      queryClient.setQueryData(queryKey, data);
      appContext.setMessage(t("common.savedSuccessfully", ));
    },
    onError: (error) => {
      setArea(error.response.data); //maybe not?
      appContext.setMessage(t("common.error"), true);
    },
  });

  // Handle updates using SEO templates pattern
  function handleUpdate(key, value) {
    console.log('📝 handleUpdate called:', { key, value });
    
    if (key === 'translations') {
      // SEOEditor passes the entire translations object - just update it directly
      console.log('🔄 Updating translations:', value);
      setArea(oldArea => {
        const newArea = { ...oldArea, translations: value };
        console.log('✅ New area after translation update:', newArea);
        return newArea;
      });
    } else {
      // Direct field update (like slug, or default locale fields)
      console.log('🔄 Updating field:', key, '=', value);
      setArea(oldArea => ({ ...oldArea, [key]: value }));
    }
  }

  // Handle name field updates with translation support (following SEO templates pattern)
  const handleNameUpdate = (value) => {
    // Deep clone translations to avoid mutation (like SEO templates)
    let newArea = { ...area };
    newArea.translations = JSON.parse(JSON.stringify(area.translations || {}));
    
    if (selectedNameLocale === currentLocale) {
      // Default locale - update main name field
      newArea.name = value;
    } else {
      // Non-default locale - update translations
      newArea.translations.name = newArea.translations.name || {};
      newArea.translations.name[selectedNameLocale] = value;
    }
    
    setArea(newArea);
  };

  // Get name value for current locale
  const getNameValue = () => {
    if (selectedNameLocale === currentLocale) {
      return area.name || "";
    } else {
      // Check both sources: area_translations (from server) and translations (being edited)
      const savedTranslation = area.area_translations?.name?.[selectedNameLocale];
      const editingTranslation = area.translations?.name?.[selectedNameLocale];
      
      // Prefer the editing translation if it exists, otherwise use saved translation
      return editingTranslation !== undefined ? editingTranslation : (savedTranslation || "");
    }
  };

  // Get placeholder text for name field
  const getNamePlaceholder = () => {
    if (selectedNameLocale === currentLocale) {
      return "e.g. United States";
    } else {
      const defaultName = area.name || "";
      return defaultName ? t("common.default", { value: defaultName }) : "e.g. United States";
    }
  };

  // Simple comparison using JSON stringify (like useCompare but more targeted)
  const areasAreEqual = (data, area) => {
    if (!data || !area) return true;
    
    // Create clean objects with just the fields we care about
    const cleanData = {
      name: data.name || '',
      page_title: data.page_title || '',
      page_description: data.page_description || '',
      meta_title: data.meta_title || '',
      meta_description: data.meta_description || '',
      translations: data.translations || {}
    };
    
    const cleanArea = {
      name: area.name || '',
      page_title: area.page_title || '',
      page_description: area.page_description || '',
      meta_title: area.meta_title || '',
      meta_description: area.meta_description || '',
      translations: area.translations || {}
    };
    
    const dataString = JSON.stringify(cleanData);
    const areaString = JSON.stringify(cleanArea);
    const isEqual = dataString === areaString;
    
    // Debug comparison
    console.log('🔍 Area comparison:', {
      isEqual,
      cleanData,
      cleanArea,
      dataString,
      areaString,
      'dataTranslations': cleanData.translations,
      'areaTranslations': cleanArea.translations,
      'translations match': JSON.stringify(cleanData.translations) === JSON.stringify(cleanArea.translations)
    });
    
    return isEqual;
  };
  
  const hasUnsavedChanges = originalData && area && !areasAreEqual(originalData, area);

  if (isError) return <p>{t("common.error")}</p>;
  if (area == null || isLoading)
    return <AreaFormSkeleton breadcrumbs={props.breadcrumbs} />;

  const { name, preview_url } = area;
  
  // Get locale information
  const isDefaultNameLocale = selectedNameLocale === currentLocale;

  // Language selector component for name field
  const nameLanguageSelector = (
    <LanguageSelector
      options={localeOptions}
      value={selectedNameLocale}
      onChange={setSelectedNameLocale}
    />
  );

  // Handle form submission (triggered by Save Bar)
  const handleSubmit = (e) => {
    e.preventDefault();
    saveArea.mutate();
  };

  // Handle form reset (triggered by Discard button in Save Bar)
  const handleReset = (e) => {
    e.preventDefault();
    setArea({ ...originalData });
  };

  return (
    <form 
      data-save-bar 
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
    <Page>
      <TitleBar
        title={name}
        breadcrumbs={props.breadcrumbs}
        secondaryActions={[
          { content: t("common.preview"), url: preview_url, external: true },
        ]}
      />
      <Layout>
        <Layout.Section>
          {/* Name field section */}
          <Card>
            <Card.Section
              title={
                <Stack distribution="equalSpacing" alignment="center">
                  <Text variant="headingMd" as="h2">{t("area.form.basicInformation")}</Text>
                  {nameLanguageSelector}
                </Stack>
              }
            >
              {isLocalizationEnabled && !isDefaultNameLocale && (
                <div style={{ marginBottom: '1rem' }}>
                                      <Text variant="bodyMd" color="subdued">
                      {t("seo.editingLocaleHelp", {
                        locale: getLocaleDisplayName(selectedNameLocale),
                        defaultLocale: getLocaleDisplayName(currentLocale)
                      })} 
                    </Text>
                </div>
              )}
              <TextField
                label={t("area.form.name")}
                placeholder={getNamePlaceholder()}
                value={getNameValue()}
                onChange={handleNameUpdate}
                error={area?.errors?.name?.join(", ")}
              />
            </Card.Section>
          </Card>
          
          <SEOEditor area={area} handleUpdate={handleUpdate} languageSettings={languageSettings} />
        </Layout.Section>
        <Layout.Section>
          <Card>
            <Card.Section>
              <Text variant="headingXs" as="h3">{t("area.form.yourLocationsIn", { name })}</Text>
            </Card.Section>
            <Card.Section flush>
              <div className="area-locations-list">
                <PlaceTable
                  isLocationList={true}
                  url={`${props.resourceUrl}/locations`}
                  renderLocation={renderLocation}
                  resourceName={{
                    singular: t("area.form.resourceName.singular", { name }),
                    plural: t("area.form.resourceName.plural", { name }),
                  }}
                  headings={[
                    { title: t("area.form.tableHeadings.name") },
                    { title: t("area.form.tableHeadings.status") },
                    { title: t("area.form.tableHeadings.city") },
                    { title: t("area.form.tableHeadings.region") },
                    { title: t("area.form.tableHeadings.country") },
                  ]}
                />
              </div>
            </Card.Section>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
    </form>
  );
}

function renderLocation(location, isSelected) {
  const { id, status, url, name, city, region, country } = location;

  return (
    <IndexTable.Row key={id} id={id} selected={isSelected}>
      <IndexTable.Cell>
        <div style={{ padding: "var(--p-space-2) 0px" }}>
          <Link url={url} monochrome removeUnderline>
            <Text  variant="bodyMd" as="span" fontWeight="semibold">{name}</Text>
          </Link>
        </div>
      </IndexTable.Cell>
      <IndexTable.Cell>
        {status == "published" ? (
          <Badge status="success">Published</Badge>
        ) : (
          <Badge>Draft</Badge>
        )}
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Link monochrome removeUnderline url={city.url}>
          {city.name}
        </Link>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Link monochrome removeUnderline url={region.url}>
          {region.name}
        </Link>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <Link monochrome removeUnderline url={country.url}>
          {country.name}
        </Link>
      </IndexTable.Cell>
    </IndexTable.Row>
  );
}
